import { EventEmitter, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Service that provides a size-reference depending on the device-resolution.
 */
@Injectable({
  providedIn: 'root'
})
export class ResolutionService {
  // see: https://getbootstrap.com/docs/3.3/css/#grid
  public serverReference = 0; // server-side-rendering: no window
  public extraSmallReference = 1; // 0   - 767  px
  public smallReference = 33; // 768 - 991  px
  public mediumReference = 66; // 992 - 1199 px
  public largeReference = 100; //    >= 1200 px

  protected currentSizeReference = this.serverReference;

  readonly sizeReferenceChange: EventEmitter<number> = new EventEmitter<number>(true);

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    if (isPlatformBrowser(platformId)) {
      this.setInitSizeReference();
    } else {
      this.currentSizeReference = this.serverReference;
    }
  }

  getCurrentSizeReference(): number {
    return this.currentSizeReference;
  }

  setInitSizeReference() {
    this.updateSizeReference(document.body.clientWidth);
  }

  updateSizeReference(width: number) {
    const currentSizeReference = this.currentSizeReference;

    if (width < 681) {
      this.currentSizeReference = this.extraSmallReference;
    }
    if (width >= 682) {
      this.currentSizeReference = this.smallReference;
    }
    if (width >= 900) {
      this.currentSizeReference = this.mediumReference;
    }
    if (width >= 1200) {
      this.currentSizeReference = this.largeReference;
    }

    if (currentSizeReference !== this.currentSizeReference) {
      this.sizeReferenceChange.emit(this.currentSizeReference);
    }
  }

  isServer(): boolean {
    return this.currentSizeReference === this.serverReference;
  }

  isExtraSmallDevice(): boolean {
    return this.currentSizeReference === this.extraSmallReference;
  }

  isSmallDevice(): boolean {
    return this.currentSizeReference === this.smallReference;
  }

  isMediumDevice(): boolean {
    return this.currentSizeReference === this.mediumReference;
  }

  isLargeDevice(): boolean {
    return this.currentSizeReference === this.largeReference;
  }

  /**
   * small- and extra-small devices
   */
  isMobileDevice(): boolean {
    return (
      this.currentSizeReference === this.serverReference || this.currentSizeReference < this.mediumReference
    );
  }

  isSmallerThanLargeDevice(): boolean {
    return (
      this.currentSizeReference === this.serverReference || this.currentSizeReference < this.largeReference
    );
  }
}
